/**
 * VeilSkillEffect — Specter veil-path active: perception interference field.
 *
 * Toggle a perception filter around the player. Enemies within radiusTiles
 * (base 4) have their detection and sound cones reduced by perceptionReduction
 * (base 20%). The debuff is maintained per-frame — enemies entering the radius
 * gain it, enemies leaving it lose it.
 *
 * Upgrade hooks (pre-wired, values resolved at each activation/update):
 * - Deep Veil (perceptionReduction +0.1, radiusTiles +1): deeper field, wider radius
 * - Afterburn (afterburnDurationMs): on deactivate, linger debuff for N ms
 * - Gaslight (permanentDetectionReduction, permanentSoundConeReduction, exposureDurationMs):
 *   track per-enemy exposure; fire gaslight_debuff at threshold seconds
 * - Feedback sound degradation (soundDegradationPerSecond, soundDegradationMax):
 *   tick per-enemy permanent sound-cone reduction every second (capped)
 *
 * Mirage evolution note: MirageSkillEffect uses the same architecture with
 * deepened base parameters (40% reduction, 6-tile radius, +15% aggro slowdown).
 * VeilSkillEffect upgrades (Deep Veil, Afterburn) inherit via the resolver's
 * scope 'veil' chain.
 *
 * Extends ToggleSkillEffect. Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Veil, Deep Veil, Afterburn, Gaslight, White Noise
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VeilDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
    /** Returns ids of all enemies currently within the given pixel radius. */
    getEnemiesInRadius(radiusPx: number): string[];
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      targetEntityId: string;
      perceptionReduction?: number;
      aggroGainMultiplier?: number;
    }): void;
    resetDuration(defId: string, targetEntityId: string, newDurationMs: number): void;
    remove(defId: string, targetEntityId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface VeilConfig {
  readonly cooldownMs: number;
  readonly baseRadiusTiles: number;
  readonly basePerceptionReduction: number;
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: VeilConfig = {
  cooldownMs: 1200,                 // medium cooldown tier
  baseRadiusTiles: 4,               // spec: 4-tile base radius
  basePerceptionReduction: 0.2,     // spec: 20% base reduction (vision + sound)
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Internal per-enemy tracking
// ---------------------------------------------------------------------------

interface EnemyTracker {
  /** Accumulated Gaslight exposure in ms (ticks while in field). */
  gaslightAccumulatorMs: number;
  /** Whether gaslight_debuff has been applied to this enemy. */
  gaslightApplied: boolean;
  /** White Noise accumulator — full seconds elapsed while in field. */
  whiteNoiseAccumulatorMs: number;
  /** Total permanent White Noise reduction applied so far (capped). */
  whiteNoiseApplied: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VeilSkillEffect extends ToggleSkillEffect {
  readonly skillId: string = 'veil';

  protected readonly deps: VeilDeps;
  protected readonly config: VeilConfig;

  // Active debuff tracking
  protected _debuffedEnemyIds: Set<string> = new Set();
  protected _enemyTrackers: Map<string, EnemyTracker> = new Map();

  // Afterburn state
  protected _inAfterburn = false;
  protected _afterburnEnemyIds: Set<string> = new Set();

  // Resolved values (set at activation and each update)
  protected _resolvedRadiusTiles = 0;
  protected _resolvedPerceptionReduction = 0;
  protected _resolvedAfterburn = 0;

  // Observable state
  private _fizzled = false;
  private _activationCount = 0;
  private _gaslightAppliedTo: string[] = [];
  private _whiteNoiseAppliedTo: string[] = [];

  constructor(deps: VeilDeps, config: Partial<VeilConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  get debuffedEnemyIds(): ReadonlySet<string> { return this._debuffedEnemyIds; }
  get inAfterburn(): boolean { return this._inAfterburn; }
  get resolvedRadiusTiles(): number { return this._resolvedRadiusTiles; }
  get resolvedPerceptionReduction(): number { return this._resolvedPerceptionReduction; }
  get resolvedAfterburn(): number { return this._resolvedAfterburn; }
  get gaslightAppliedTo(): readonly string[] { return this._gaslightAppliedTo; }
  get whiteNoiseAppliedTo(): readonly string[] { return this._whiteNoiseAppliedTo; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    if (this._inAfterburn) return false;
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this.cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    this._isActive = true;
    this._inAfterburn = false;
    this._debuffedEnemyIds.clear();
    this._enemyTrackers.clear();
    this._gaslightAppliedTo = [];
    this._whiteNoiseAppliedTo = [];

    // Resolve upgrade parameters
    this._resolveUpgrades();

    this._activationCount++;
    return true;
  }

  protected onDeactivate(): void {
    this._resolvedAfterburn = this.deps.resolver.getAdditiveEffect('veil', 'afterburnDurationMs');

    if (this._resolvedAfterburn > 0 && this._debuffedEnemyIds.size > 0) {
      // Afterburn: linger debuff for afterburnDurationMs before removal
      this._inAfterburn = true;
      this._afterburnEnemyIds = new Set(this._debuffedEnemyIds);
      for (const enemyId of this._afterburnEnemyIds) {
        this.deps.statusEffects.resetDuration(
          'veil_debuff', enemyId, this._resolvedAfterburn,
        );
      }
    } else {
      // Immediate removal
      for (const enemyId of this._debuffedEnemyIds) {
        this.deps.statusEffects.remove('veil_debuff', enemyId);
      }
    }

    this._debuffedEnemyIds.clear();
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    this._resolveUpgrades();

    const radiusPx = this._resolvedRadiusTiles * this.config.tileSizePx;
    const currentEnemies = new Set(this.deps.targeting.getEnemiesInRadius(radiusPx));

    // Enemies that left the radius — remove debuff
    for (const enemyId of this._debuffedEnemyIds) {
      if (!currentEnemies.has(enemyId)) {
        this.deps.statusEffects.remove('veil_debuff', enemyId);
        this._debuffedEnemyIds.delete(enemyId);
        this._enemyTrackers.delete(enemyId);
      }
    }

    // Enemies in radius — apply or maintain debuff
    for (const enemyId of currentEnemies) {
      if (!this._debuffedEnemyIds.has(enemyId)) {
        // New enemy entered radius
        // STUB: deferred — no enemy perception AI reads this debuff
        this.deps.statusEffects.apply('veil_debuff', {
          sourceSkillId: this.skillId,
          durationMs: -1, // indefinite while in radius
          targetEntityId: enemyId,
          perceptionReduction: this._resolvedPerceptionReduction,
        });
        this._debuffedEnemyIds.add(enemyId);
        this._enemyTrackers.set(enemyId, {
          gaslightAccumulatorMs: 0,
          gaslightApplied: false,
          whiteNoiseAccumulatorMs: 0,
          whiteNoiseApplied: 0,
        });
      }

      // Tick Gaslight and White Noise accumulators
      const tracker = this._enemyTrackers.get(enemyId)!;
      this._tickGaslight(enemyId, tracker, delta);
      this._tickWhiteNoise(enemyId, tracker, delta);
    }
  }

  override update(delta: number): void {
    super.update(delta);

    // Tick afterburn: once the debuff durations expire, clear state
    if (this._inAfterburn) {
      // The StatusEffectSystem handles expiry; we just need to clear our state
      // when afterburn is complete. Use a timer approach.
      if (!this._afterburnTimerActive) {
        this._afterburnTimerMs = this._resolvedAfterburn;
        this._afterburnTimerActive = true;
      }
      this._afterburnTimerMs = Math.max(0, this._afterburnTimerMs - delta);
      if (this._afterburnTimerMs <= 0) {
        this._inAfterburn = false;
        this._afterburnTimerActive = false;
        this._afterburnEnemyIds.clear();
        this.cooldownRemaining = this.config.cooldownMs;
      }
    }
  }

  private _afterburnTimerMs = 0;
  private _afterburnTimerActive = false;

  // --- Protected: upgrade hooks ---

  protected _resolveUpgrades(): void {
    const deepVeilReduction = this.deps.resolver.getAdditiveEffect('veil', 'perceptionReduction');
    const deepVeilRadius = this.deps.resolver.getAdditiveEffect('veil', 'radiusTiles');
    this._resolvedPerceptionReduction = this.config.basePerceptionReduction + deepVeilReduction;
    this._resolvedRadiusTiles = this.config.baseRadiusTiles + deepVeilRadius;
    this._resolvedAfterburn = this.deps.resolver.getAdditiveEffect('veil', 'afterburnDurationMs');
  }

  private _tickGaslight(enemyId: string, tracker: EnemyTracker, delta: number): void {
    if (tracker.gaslightApplied) return;

    const exposureThreshold = this.deps.resolver.getTreePassiveEffect(
      'specter', 'gaslightExposureDurationMs', 'max',
    );
    if (exposureThreshold <= 0) return; // Gaslight not unlocked

    tracker.gaslightAccumulatorMs += delta;
    if (tracker.gaslightAccumulatorMs >= exposureThreshold) {
      const detectionReduction = this.deps.resolver.getTreePassiveEffect(
        'specter', 'gaslightDetectionReduction', 'max',
      );
      const soundReduction = this.deps.resolver.getTreePassiveEffect(
        'specter', 'gaslightSoundConeReduction', 'max',
      );
      // STUB: deferred — no enemy perception AI reads gaslight debuffs
      this.deps.statusEffects.apply('gaslight_debuff', {
        sourceSkillId: this.skillId,
        durationMs: -1, // permanent
        targetEntityId: enemyId,
        perceptionReduction: detectionReduction,
      });
      this.deps.statusEffects.apply('gaslight_sound_debuff', {
        sourceSkillId: this.skillId,
        durationMs: -1,
        targetEntityId: enemyId,
        perceptionReduction: soundReduction,
      });
      tracker.gaslightApplied = true;
      if (!this._gaslightAppliedTo.includes(enemyId)) {
        this._gaslightAppliedTo.push(enemyId);
      }
    }
  }

  private _tickWhiteNoise(enemyId: string, tracker: EnemyTracker, delta: number): void {
    const degradationPerSecond = this.deps.resolver.getAdditiveEffect(
      'broadcast', 'soundDegradationPerSecond',
    );
    if (degradationPerSecond <= 0) return; // Feedback sound degradation not unlocked

    const maxReduction = this.deps.resolver.getAdditiveEffect(
      'broadcast', 'soundDegradationMax',
    );

    if (tracker.whiteNoiseApplied >= maxReduction) return; // already capped

    tracker.whiteNoiseAccumulatorMs += delta;
    const secondsElapsed = Math.floor(tracker.whiteNoiseAccumulatorMs / 1000);
    const prevSeconds = Math.floor((tracker.whiteNoiseAccumulatorMs - delta) / 1000);

    if (secondsElapsed > prevSeconds) {
      const ticksThisUpdate = secondsElapsed - prevSeconds;
      for (let i = 0; i < ticksThisUpdate; i++) {
        if (tracker.whiteNoiseApplied >= maxReduction) break;
        const increment = Math.min(degradationPerSecond, maxReduction - tracker.whiteNoiseApplied);
        tracker.whiteNoiseApplied += increment;
        // STUB: deferred — no enemy hearing system reads white noise debuff
        this.deps.statusEffects.apply('white_noise_debuff', {
          sourceSkillId: this.skillId,
          durationMs: -1, // permanent
          targetEntityId: enemyId,
          perceptionReduction: increment,
        });
        if (!this._whiteNoiseAppliedTo.includes(enemyId)) {
          this._whiteNoiseAppliedTo.push(enemyId);
        }
      }
    }
  }
}
